import { List } from '@faststore/ui'
import { useEffect, useRef, useState } from 'react'
import { XCloseGlobal } from 'src/components/icons/XCloseGlobal'
import { AdditionalProperty } from 'src/components/sections/NewPDP/typings'
import Button, { ButtonIcon } from 'src/components/ui/Button'
import SlideOver from 'src/components/ui/SlideOver'
import { useCart } from 'src/sdk/cart/useCart'
import { useCheckoutButton } from 'src/sdk/cart/useCheckoutButton'
import type { CartItem as CartItemType } from 'src/sdk/cart/validate'
import { useUI } from 'src/sdk/ui'
import { useModal } from 'src/sdk/ui/modal/Provider'
import CartItem from '../CartItem'
import EmptyCart from '../EmptyCart'
import OrderSummary from '../OrderSummary'
import './cart-sidebar.scss'
import { getLoyaltyMultiplier } from './utils/utils'

const getLoyaltyMultipliers = async (skuIds: string[]) => {
  const multipliers = await Promise.all(
    skuIds.map((sku) => getLoyaltyMultiplier(sku))
  )

  return multipliers
}

const calculateTotalLoyaltyPoints = (
  multipliers: string[],
  items: CartItemType[]
) => {
  const notValid =
    !items || items.length === 0 || !multipliers || multipliers.length === 0
  if (notValid) {
    return 0
  }

  return items.reduce((sum: number, item: CartItemType) => {
    const subscriptions = item?.itemOffered?.additionalProperty?.filter(
      (el: AdditionalProperty) =>
        el.valueReference === 'ATTACHMENT' &&
        el.name.includes('vtex.subscription')
    )

    const targetItem = multipliers.find((el) => {
      const [sku] = el.split('-')
      return sku === item?.itemOffered?.sku
    })
    const loyaltyMultiplier = targetItem
      ? parseFloat(targetItem.split('-')[1])
      : 1

    let itemLoyaltyPoints = 0

    itemLoyaltyPoints =
      subscriptions?.length > 0
        ? item.price * item.quantity * 0.95
        : item.price * item.quantity

    itemLoyaltyPoints =
      loyaltyMultiplier <= 1
        ? itemLoyaltyPoints
        : Math.trunc(itemLoyaltyPoints) * loyaltyMultiplier

    return sum + itemLoyaltyPoints
  }, 0)
}

function CartSidebar() {
  const btnProps = useCheckoutButton()
  const cart = useCart()
  const { displayMinicart, closeMinicart } = useUI()
  const { onModalClose } = useModal()

  const { items, isValidating: isCartValidating, total } = cart
  const [productOutOfStockPrice, setProductOutOfStockPrice] = useState(Number)
  const [activeProducts, setActiveProducts] = useState([])
  const [activeProductsQuantity, setActiveProductsQuantity] = useState(Number)
  const [isVTEXValidating, setIsVTEXValidating] = useState(Boolean)
  const [hasOutOfStock, setHasOutOfStock] = useState(Boolean)
  const [loyaltyMultipliers, setLoyaltyMultipliers] = useState<string[]>([])
  const [originalPricePerItem, setOriginalPricePerItem] = useState<
    Record<string, number>
  >({})

  const handleSubTotal = (sku: string, originalPrice: number) => {
    setOriginalPricePerItem((prev) => {
      const newState = { ...prev }
      newState[sku] = originalPrice
      return newState
    })
  }

  const fetchRef = useRef(false)

  useEffect(() => {
    const cartItemsArray: any[] = []
    const activeProductsArray: any = []
    let activeProductsQuant = 0
    let OutOfStockPrice = 0

    cartItemsArray.push(items)

    cartItemsArray[0].map((item: any) => {
      const itemSku = item?.itemOffered?.sku

      const itemVariantFiltered =
        item?.itemOffered?.isVariantOf?.hasVariant?.filter(
          (i: any) => i?.sku === itemSku
        )

      if (itemVariantFiltered[0]?.offers?.lowPrice <= 0) {
        OutOfStockPrice += item?.price * item?.quantity
      } else {
        const itemQuantity: number = item?.quantity

        activeProductsQuant += itemQuantity
        activeProductsArray.push(item)
      }

      return null
    })

    setProductOutOfStockPrice(OutOfStockPrice)
    // setActiveProducts(activeProductsArray)
    setActiveProducts((prev) => {
      const prevState = JSON.stringify(prev)
      const nextState = JSON.stringify(activeProductsArray)

      if (prevState !== nextState) return activeProductsArray

      return prev
    })
    setActiveProductsQuantity(activeProductsQuant)

    setHasOutOfStock(OutOfStockPrice > 0)
  }, [cart, items, isCartValidating])

  useEffect(() => {
    if (items && items.length > 0 && !fetchRef.current) {
      const getLoyaltyData = async () => {
        const skuIds = items.map((item) => item?.itemOffered?.sku)
        const loyaltyMultipliers = await getLoyaltyMultipliers(skuIds)
        setLoyaltyMultipliers(loyaltyMultipliers)
      }

      getLoyaltyData()
      fetchRef.current = true
    }
  }, [items])

  const totalLoyaltyPoints = calculateTotalLoyaltyPoints(
    loyaltyMultipliers,
    activeProducts
  )
  const formattedLoyaltyPoints = Math.trunc(totalLoyaltyPoints)

  const isEmpty = items.length === 0

  const subTotal = Object.entries(originalPricePerItem).reduce((acc, curr) => {
    if (items.find((item) => item.itemOffered.sku === curr[0])) {
      return acc + curr[1]
    }
    return acc
  }, 0)

  const finalTotal = total - productOutOfStockPrice
  const realDiscount = subTotal - finalTotal
  const isUnderPrice = finalTotal < 8.99
  const isDisabled = Boolean(
    isCartValidating || isVTEXValidating || isUnderPrice || btnProps?.isPending
  )

  return (
    <SlideOver
      isOpen={displayMinicart}
      onDismiss={closeMinicart}
      size="full"
      direction="rightSide"
      className="cart-sidebar"
    >
      <header data-testid="cart-sidebar">
        <div className="cart-sidebar__title">
          <p className="text__lead">Il tuo carrello</p>
        </div>
        <ButtonIcon
          data-testid="cart-sidebar-button-close"
          className="cursor-pointer"
          aria-label="Close Cart"
          icon={<XCloseGlobal className={'w-[30px]'} />}
          onClick={onModalClose}
        />
      </header>

      {(hasOutOfStock || (isUnderPrice && !isEmpty)) && (
        <div className="outOfStock__slider-message-container">
          <ul>
            {hasOutOfStock && (
              <li>
                Nel carrello è presente un articolo al momento non disponibile.
                <span className="strongText-out"> Rimuovi l'articolo</span> per
                procedere all'acquisto
              </li>
            )}
            {isUnderPrice && !isEmpty && (
              <li>
                Attenzione: il valore minimo d'ordine è di{' '}
                <span className="strongText-out">8,99€</span> (spese di consegna
                escluse)
              </li>
            )}
          </ul>
        </div>
      )}

      {isEmpty ? (
        <EmptyCart onDismiss={onModalClose} />
      ) : (
        <>
          <List
            className={
              isCartValidating || isVTEXValidating
                ? 'cart-sidebar-list-validating'
                : ''
            }
          >
            {isCartValidating || isVTEXValidating ? (
              <div className="loader-wrapper">
                <div className="loader" />
              </div>
            ) : null}
            {items.map((item) => (
              <li key={item.id}>
                <CartItem
                  item={item}
                  loyaltyMultipliers={loyaltyMultipliers}
                  isCartValidating={Boolean(
                    isCartValidating || isVTEXValidating
                  )}
                  setIsVTEXValidating={setIsVTEXValidating}
                  handleSubTotal={handleSubTotal}
                />
              </li>
            ))}
          </List>

          <footer className="z-50">
            <OrderSummary
              realDiscount={realDiscount}
              subTotal={subTotal}
              total={finalTotal}
              numberOfItems={activeProductsQuantity}
              checkoutButton={
                <Button
                  variant="primary"
                  {...btnProps}
                  className={`${isUnderPrice ? 'disabledForPrice' : ''}`}
                  disabled={isDisabled}
                >
                  {isCartValidating || isVTEXValidating || btnProps?.isPending
                    ? 'Caricamento in corso...'
                    : 'Vai alla cassa'}
                </Button>
              }
              loyaltyPoints={formattedLoyaltyPoints}
            />
          </footer>
        </>
      )}
    </SlideOver>
  )
}

export default CartSidebar
